import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { CheckCircle2, PenLine } from "lucide-react";
import SignaturePadField from "./SignaturePadField";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import DateFieldInput from "./DateFieldInput";
import {
  PAI_FIRMA_ROLES,
  collectPaiFirmePayload,
  countPaiFirmeCompilate,
  isPaiFirmaRoleSigned,
  mergeActiveSignatureIntoState,
} from "../config/paiFirme";

/**
 * Step finale wizard PAI: raccolta firme digitali (touch / tavoletta Wacom STU).
 * Un solo canvas visibile per evitare problemi di dimensione su canvas nascosti.
 */
const PaiFirmeStep = forwardRef(function PaiFirmeStep({ firmeState, onFirmeChange, defaultSignerName = "" }, ref) {
  const activeSigRef = useRef(null);
  const [activeRole, setActiveRole] = useState(PAI_FIRMA_ROLES[0]?.key || "oss");
  const [, setTick] = useState(0);

  const activeMeta = PAI_FIRMA_ROLES.find((r) => r.key === activeRole);

  const persistActiveSignature = () => mergeActiveSignatureIntoState(firmeState, activeRole, activeSigRef);

  const refreshSigned = () => setTick((n) => n + 1);

  const selectRole = (key) => {
    if (key === activeRole) return;
    onFirmeChange(persistActiveSignature());
    setActiveRole(key);
  };

  const updateFigure = (roleKey, field, value) => {
    onFirmeChange({
      ...firmeState,
      figure: {
        ...firmeState.figure,
        [roleKey]: {
          ...firmeState.figure?.[roleKey],
          [field]: value,
        },
      },
    });
  };

  const handleSignatureChange = () => {
    const refPad = activeSigRef.current;
    if (refPad?.isEmpty()) {
      updateFigure(activeRole, "png", null);
    }
    refreshSigned();
  };

  useEffect(() => {
    const refPad = activeSigRef.current;
    if (!refPad) return;
    const png = firmeState.figure?.[activeRole]?.png;
    const frame = requestAnimationFrame(() => {
      refPad.clear();
      if (png) refPad.fromDataURL(png);
    });
    return () => cancelAnimationFrame(frame);
  }, [activeRole]); // eslint-disable-line react-hooks/exhaustive-deps -- ripristina il pad solo al cambio ruolo, non a ogni stroke

  useImperativeHandle(ref, () => ({
    collectFirme: () => collectPaiFirmePayload(persistActiveSignature()),
    countSigned: () => countPaiFirmeCompilate(persistActiveSignature()),
  }));

  const stateForCount = persistActiveSignature();
  const signedCount = countPaiFirmeCompilate(stateForCount);

  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-purple-700 border-b pb-2 flex items-center gap-2">
        <PenLine className="w-4 h-4" />
        Firme delle figure attinenti
      </h3>
      <p className="text-sm text-slate-600">
        Ogni professionista o familiare può firmare sul dispositivo (dito o mouse) o con tavoletta Wacom STU.
        Le firme sono opzionali: puoi salvare il PAI anche se non tutte le figure sono presenti.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>Luogo firma</Label>
          <Input
            value={firmeState.luogo || ""}
            onChange={(e) => onFirmeChange({ ...firmeState, luogo: e.target.value })}
            placeholder="Es. Milano"
          />
        </div>
        <div>
          <Label>Data firma</Label>
          <DateFieldInput
            value={firmeState.data || ""}
            onChange={(v) => onFirmeChange({ ...firmeState, data: v })}
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {PAI_FIRMA_ROLES.map(({ key, label }) => {
          const isSigned = isPaiFirmaRoleSigned(stateForCount, activeRole, activeSigRef, key);
          return (
            <button
              key={key}
              type="button"
              onClick={() => selectRole(key)}
              className={`px-3 py-1.5 text-xs rounded-full border transition-all flex items-center gap-1.5 ${
                activeRole === key
                  ? "bg-purple-600 text-white border-purple-600"
                  : isSigned
                    ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                    : "bg-slate-50 text-slate-600 border-slate-200 hover:border-purple-300"
              }`}
            >
              {isSigned && <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />}
              {label}
            </button>
          );
        })}
      </div>

      {activeMeta && (
        <div className="rounded-lg border border-purple-200 bg-purple-50/40 p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <p className="font-medium text-purple-900">{activeMeta.label}</p>
            <span className="text-xs text-slate-500">
              {signedCount}/{PAI_FIRMA_ROLES.length} firmate
            </span>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Nome e cognome firmatario</Label>
            <Input
              value={firmeState.figure?.[activeRole]?.firmato_da || ""}
              onChange={(e) => updateFigure(activeRole, "firmato_da", e.target.value)}
              placeholder={defaultSignerName || "Nome cognome"}
              className="mt-1"
            />
          </div>
          <SignaturePadField
            key={activeRole}
            ref={activeSigRef}
            canvasStyle={{ width: "100%", height: 160 }}
            watermark="Firma qui con dito, mouse o tavoletta"
            clearVariant="link"
            actionsClassName="flex flex-wrap items-center justify-between gap-2 mt-2"
            onSignatureChange={handleSignatureChange}
          />
          <p className="text-xs text-slate-500">
            Passa alla figura successiva con i pulsanti in alto; la firma viene salvata automaticamente.
          </p>
        </div>
      )}
    </div>
  );
});

export default PaiFirmeStep;
